import { useCallback, useEffect, useRef, useState, type SyntheticEvent } from 'react';
import { createPortal } from 'react-dom';
import { APP_NAME, SNAP_MAX_VIDEO_SECONDS } from '../../../shared/constants';
import {
  RECORD_AUDIO_BITS,
  RECORD_VIDEO_BITS,
  cameraConstraints,
  recordedFile,
  recordingType,
} from '../media/record';
import { useBackCloses } from '../router';
import { IconClose, IconFlip } from '../ui';

type Facing = 'environment' | 'user';

const keep = (event: SyntheticEvent) => event.stopPropagation();

/**
 * The Video button's camera: the app's own, recording at 1080p.
 *
 * Opened through a file input, an iPhone recorded a snap at its "medium"
 * quality, about 1 Mbps -- Aana's clip came through soft enough that Carter
 * asked for at least 1080p. Recording here lets the page pick the size and the
 * bitrate. One button, tap to start and tap to stop; it stops itself at the
 * snap limit. If the camera cannot be had, the phone's own is one tap away.
 */
export function VideoRecorder({
  onRecorded,
  onCancel,
  onUsePhoneCamera,
  now = () => Date.now(),
}: {
  onRecorded: (file: File) => void;
  onCancel: () => void;
  /** Called from a tap, so the file input it opens is allowed to open. */
  onUsePhoneCamera: () => void;
  now?: () => number;
}) {
  const preview = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const [facing, setFacing] = useState<Facing>('environment');
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);

  const letGo = () => {
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
  };

  // The camera, opened for the side asked for; closed on leaving or flipping.
  useEffect(() => {
    let live = true;
    setReady(false);
    navigator.mediaDevices
      .getUserMedia(cameraConstraints(facing))
      .then((got) => {
        if (!live) {
          got.getTracks().forEach((track) => track.stop());
          return;
        }
        stream.current = got;
        if (preview.current) preview.current.srcObject = got;
        setReady(true);
      })
      .catch((err: unknown) => {
        if (!live) return;
        const name = (err as { name?: string }).name ?? '';
        setFailed(
          name === 'NotAllowedError'
            ? `${APP_NAME} is not allowed to use the camera. Allow it in your phone’s settings, or use the phone’s camera.`
            : 'The camera would not start. You can use the phone’s camera instead.',
        );
      });
    return () => {
      live = false;
      letGo();
    };
  }, [facing]);

  /*
   * Taken away mid-clip -- the sheet under it closed -- the clip goes with it.
   * Its stop would otherwise hand the clip to a sheet that has gone, and it
   * turned up ready to send the next time a snap was started.
   */
  useEffect(
    () => () => {
      const made = recorder.current;
      recorder.current = null;
      if (!made) return;
      made.onstop = null;
      made.stop();
    },
    [],
  );

  const stop = useCallback(() => {
    recorder.current?.stop();
    recorder.current = null;
  }, []);

  // The clock, and the stop at the snap limit.
  useEffect(() => {
    if (startedAt === null) return;
    const tick = window.setInterval(() => {
      const seconds = (now() - startedAt) / 1000;
      setElapsed(seconds);
      if (seconds >= SNAP_MAX_VIDEO_SECONDS) stop();
    }, 200);
    return () => window.clearInterval(tick);
  }, [startedAt, now, stop]);

  const start = () => {
    if (!stream.current) return;
    const type = recordingType();
    const made = new MediaRecorder(stream.current, {
      ...(type ? { mimeType: type } : {}),
      videoBitsPerSecond: RECORD_VIDEO_BITS,
      audioBitsPerSecond: RECORD_AUDIO_BITS,
    });
    const parts: Blob[] = [];
    made.ondataavailable = (event) => {
      if (event.data.size) parts.push(event.data);
    };
    made.onstop = () => {
      setStartedAt(null);
      letGo();
      onRecorded(recordedFile(parts, made.mimeType || type || ''));
    };
    recorder.current = made;
    // In pieces each second, so a long clip is not one buffer at the end.
    made.start(1000);
    setStartedAt(now());
    setElapsed(0);
  };

  const recording = startedAt !== null;

  const cancel = () => {
    if (recording) {
      // Leaving mid-clip throws the clip away rather than sending it.
      if (recorder.current) recorder.current.onstop = null;
      recorder.current?.stop();
      recorder.current = null;
    }
    letGo();
    onCancel();
  };
  useBackCloses(cancel);
  const clock = (seconds: number) => `0:${String(Math.floor(seconds)).padStart(2, '0')}`;

  return createPortal(
    /*
     * Its presses stay here. It is drawn over everything but sits inside the
     * snap sheet in the tree, so a press would reach the sheet's pull-to-close
     * on its way up: a finger dragged down the camera closed the sheet.
     */
    <div
      className="recorder"
      role="dialog"
      aria-label="Record a video"
      onPointerDown={keep}
      onPointerMove={keep}
      onPointerUp={keep}
      onPointerCancel={keep}
    >
      <video
        ref={preview}
        className={facing === 'user' ? 'recorder-preview is-mirrored' : 'recorder-preview'}
        autoPlay
        muted
        playsInline
      />

      <div className="recorder-top">
        <button
          type="button"
          className="recorder-close"
          aria-label="Cancel"
          onClick={cancel}
        >
          <IconClose size={22} />
        </button>
        {recording && (
          <span className="recorder-clock" role="timer">
            {clock(elapsed)} / {clock(SNAP_MAX_VIDEO_SECONDS)}
          </span>
        )}
      </div>

      {failed ? (
        <div className="recorder-failed">
          <p>{failed}</p>
          <button type="button" className="btn" onClick={onUsePhoneCamera}>
            Use the phone&rsquo;s camera
          </button>
        </div>
      ) : (
        <div className="recorder-bottom">
          <button
            type="button"
            className="recorder-flip"
            aria-label={facing === 'environment' ? 'Switch to the front camera' : 'Switch to the back camera'}
            disabled={recording || !ready}
            onClick={() => setFacing((side) => (side === 'environment' ? 'user' : 'environment'))}
          >
            <IconFlip size={24} />
          </button>
          <button
            type="button"
            className={recording ? 'recorder-shutter is-recording' : 'recorder-shutter'}
            aria-label={recording ? 'Stop recording' : 'Start recording'}
            disabled={!ready}
            onClick={recording ? stop : start}
          >
            <span className="recorder-shutter-mark" />
          </button>
          <span className="recorder-spacer" />
        </div>
      )}
    </div>,
    document.body,
  );
}
